import React, { useEffect, Suspense, lazy } from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import Navbar from "./components/Navbar";
import Login from "./components/Login";
import { YearProvider } from "./contexts/YearProvider";
import { LeagueProvider } from "./contexts/LeagueProvider";
import PrivateRoute from "./components/PrivateRoute";
import ProtectedRoute from "./components/ProtectedRoute"; // Import the ProtectedRoute component
import LeagueRequiredRoute from "./components/LeagueRequiredRoute"; // Import the LeagueRequiredRoute component
import { useAuthState } from "react-firebase-hooks/auth";
import { auth } from "./services/firebase";
import { cacheManager } from "./services/cacheManager";

// Route-level code splitting: heavy pages (and their dependencies, e.g. the drag-and-drop
// library on the predictions page) are only downloaded when their route is actually visited.
const HomePage = lazy(() => import("./pages/HomePage"));
const TableComps = lazy(() => import("./pages/TableComps"));
const PredictionsTable = lazy(() => import("./pages/PredictionsTable"));
const Scoring = lazy(() => import("./pages/Scoring"));
const PreviousStandings = lazy(() => import("./pages/PreviousStandings"));
const Profile = lazy(() => import("./pages/Profile"));
const LeagueOnboarding = lazy(() => import("./pages/LeagueOnboarding"));
const MigrationAdmin = lazy(() => import("./pages/MigrationAdmin"));

// Import admin utils for debugging (only in development)
if (process.env.NODE_ENV === 'development') {
  import('./utils/adminUtils');
}

const RouteFallback = () => (
  <div className="min-h-screen bg-base-200 flex items-center justify-center">
    <div className="loading loading-spinner loading-lg text-primary"></div>
  </div>
);

function App() {
  const [user] = useAuthState(auth);

  // Initialize cache manager on app start
  useEffect(() => {
    cacheManager.init();

    return () => {
      cacheManager.cleanup();
    };
  }, []);

  return (
    <div className="App">
      <LeagueProvider>
        <YearProvider>
          <Router>
            {user && <Navbar />} {/* Render Navbar only if user is logged in */}
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<HomePage />} /> {/* Default route */}
                <Route path="/login" element={<Login />} />
                <Route element={<PrivateRoute />}>
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/league-onboarding" element={<LeagueOnboarding />} />
                  <Route path="/migration-admin" element={<MigrationAdmin />} />
                  <Route element={<LeagueRequiredRoute />}>
                    <Route path="/compare" element={<TableComps />} />
                    <Route element={<ProtectedRoute />}>
                      <Route path="/predictions" element={<PredictionsTable />} />
                    </Route>
                    <Route path="/scoring" element={<Scoring />} />
                    <Route
                      path="/previous-standings"
                      element={<PreviousStandings />}
                    />
                  </Route>
                </Route>
              </Routes>
            </Suspense>
          </Router>
        </YearProvider>
      </LeagueProvider>
    </div>
  );
}

export default App;
